import { useReducer } from 'react';
import { ArrayUtils } from 'record-it-utils/utils/ArrayUtils/ArrayUtils';
import { useEventCallback } from '../useEventCallback';
import { useRefWithInitializationFunction } from '../useRefWithInitializationFunction';

/**
 * A wrapper around a function call, to ensure it is only called as often as necessary.
 * Compared to a solution with useEffect, it also prevents unnecessary rerenders and allows to imperatively update the function/result again.
 */
export function useFunctionResult<T extends (...a: Array<any>) => any>(options: UseFunctionResultOptions<T>): UseFunctionResultReturnValue<T> {
  const [, rerender] = useReducer(x => x + 1, 0);

  // the last call is stored in here so it can be reproduced later on (update)
  // the function gets called immediately on the first initialization, but later updates are handled below
  const lastCallInfoRef = useRefWithInitializationFunction<{
    func: T;
    result: ReturnType<T>;
    funcArgs: Parameters<T>;
  }>(() => ({
    result: options.func(...options.funcArgs),
    func: options.func,
    funcArgs: options.funcArgs
  }));

  /**
   * call the func and update the lastCallInfoRef if the func returned a different result
   * returns true when a new result was produced
   */
  const callFunc = (func: T, funcArgs: Parameters<T>): boolean => {
    const result = func(...funcArgs);
    const resultsAreEqual = options.resultsAreEqual ?? ((a, b) => a === b);
    if (!resultsAreEqual(result, lastCallInfoRef.current.result)) {
      lastCallInfoRef.current = {
        func,
        funcArgs,
        result
      };
      return true;
    }
    return false;
  };

  // update the result if the func/funcArgs have changed
  if (lastCallInfoRef.current.func !== options.func || !ArrayUtils.areShallowEqual(lastCallInfoRef.current.funcArgs, options.funcArgs)) {
    callFunc(options.func, options.funcArgs);
  }

  // imperative way to update the function
  const update = useEventCallback(() => {
    if (callFunc(lastCallInfoRef.current.func, lastCallInfoRef.current.funcArgs)) {
      rerender();
    }
  });
  return {
    result: lastCallInfoRef.current.result,
    update
  };
}
export type UseFunctionResultOptions<T extends (...a: Array<any>) => any> = {
  func: T;
  funcArgs: Parameters<T>;
  resultsAreEqual?: (a: ReturnType<T>, b: ReturnType<T>) => boolean;
};
export type UseFunctionResultReturnValue<T extends (...a: Array<any>) => any> = {
  result: ReturnType<T>;

  /** a call to this will force the function to be called again and the result to be updated */
  update: () => void;
};